import React, { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { BUTTON_RESET, C, FONT_BODY, FOCUS_RING, RADIUS, TYPE } from "../../utils/designTokens";

// The checkout's building blocks: numbered step cards, option cards and chips
// that are real radio buttons, the wallet switch, and the shared field styles.

export const money = (n, symbol = "$") => `${symbol}${(Math.round((Number(n) || 0) * 100) / 100).toFixed(2)}`;

export const labelSx = { fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600, lineHeight: 1.3, color: C.ink };
export const metaSx = { fontFamily: FONT_BODY, fontSize: 13, fontWeight: 500, lineHeight: 1.4, color: C.muted };

// Inputs, selects and the instructions box.
export const fieldSx = {
  width: "100%",
  minHeight: 44,
  boxSizing: "border-box",
  px: 1.5,
  borderRadius: RADIUS.control,
  border: `1px solid ${C.borderStrong}`,
  bgcolor: C.bg,
  fontFamily: FONT_BODY,
  // 16px on phones so iOS doesn't zoom into the field.
  fontSize: { xs: 16, md: 14 },
  color: C.ink,
  outline: "none",
  "&::placeholder": { color: C.muted, opacity: 1 },
  "&:focus": { borderColor: C.ink, boxShadow: `0 0 0 1px ${C.ink}` },
  "&:disabled": { bgcolor: C.surface, color: C.muted },
};

// A red text button ("Change", "Edit", "Clear") with a 44px hit area.
export const textButtonSx = {
  ...BUTTON_RESET,
  position: "relative",
  fontFamily: FONT_BODY,
  fontSize: 14,
  fontWeight: 600,
  color: C.redLink,
  borderRadius: "6px",
  textDecoration: "underline",
  textUnderlineOffset: "3px",
  textDecorationColor: "transparent",
  "&:hover": { textDecorationColor: C.redLink },
  "&::before": { content: '""', position: "absolute", inset: "-12px -8px" },
  ...FOCUS_RING,
};

/** One numbered step: black circle, Bricolage title, an optional action. */
export function StepCard({ number, title, action, children, id }) {
  const headingId = `${id}-title`;
  return (
    <Box
      component="section"
      id={id}
      aria-labelledby={headingId}
      sx={{ p: { xs: 2, sm: 3 }, borderRadius: RADIUS.card, border: `1px solid ${C.border}`, bgcolor: C.bg }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
        <Box
          aria-hidden="true"
          sx={{
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: "50%",
            bgcolor: C.ink,
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: FONT_BODY,
            fontSize: 15,
            fontWeight: 700,
          }}
        >
          {number}
        </Box>
        <Box component="h2" id={headingId} sx={{ ...TYPE.sectionTitle, m: 0, flex: 1, minWidth: 0, color: C.ink }}>
          {title}
        </Box>
        {action}
      </Box>
      {children}
    </Box>
  );
}

/**
 * A radio group whose arrow keys move between its options and choose them,
 * with one tab stop: the chosen option, or the first one when none is.
 */
export function RadioGroup({ label, labelledBy, children, sx }) {
  const ref = useRef(null);
  const radios = () => Array.from(ref.current?.querySelectorAll('[role="radio"]') || []).filter(
    (el) => el.getAttribute("aria-disabled") !== "true"
  );

  // Set on the DOM after every render: React leaves a tabIndex alone while its
  // own prop is unchanged, so a stop given to the first option while nothing
  // was chosen would otherwise outlive the choice.
  useEffect(() => {
    const all = radios();
    const anyChecked = all.some((el) => el.getAttribute("aria-checked") === "true");
    all.forEach((el, i) => {
      el.tabIndex = el.getAttribute("aria-checked") === "true" || (!anyChecked && i === 0) ? 0 : -1;
    });
  });

  const onKeyDown = (e) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
    if (!step) return;
    const all = radios();
    const at = all.indexOf(document.activeElement);
    if (at === -1) return;
    e.preventDefault();
    const next = all[(at + step + all.length) % all.length];
    next.focus();
    next.click();
  };

  return (
    <Box
      ref={ref}
      role="radiogroup"
      aria-label={labelledBy ? undefined : label}
      aria-labelledby={labelledBy}
      onKeyDown={onKeyDown}
      sx={sx}
    >
      {children}
    </Box>
  );
}

/**
 * A large selectable card: icon, title, a line under it. Selected = 2px red
 * border and a red tint. The extra 1px is an inset shadow, so choosing a card
 * never shifts the layout.
 */
export function OptionCard({ checked, disabled, onSelect, icon, title, caption, captionColor, trailing, ariaLabel, sx }) {
  return (
    <Box
      component="button"
      type="button"
      role="radio"
      aria-checked={checked ? "true" : "false"}
      aria-disabled={disabled ? "true" : undefined}
      aria-label={ariaLabel}
      tabIndex={checked && !disabled ? 0 : -1}
      onClick={disabled ? undefined : onSelect}
      sx={{
        ...BUTTON_RESET,
        width: "100%",
        minHeight: 64,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.5,
        boxSizing: "border-box",
        borderRadius: RADIUS.controlLg,
        border: `1px solid ${checked && !disabled ? C.red : C.borderStrong}`,
        boxShadow: checked && !disabled ? `inset 0 0 0 1px ${C.red}` : "none",
        bgcolor: checked && !disabled ? C.redTint : C.bg,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "border-color .15s ease, background-color .15s ease",
        "&:hover": disabled || checked ? {} : { borderColor: C.ink },
        ...FOCUS_RING,
        ...sx,
      }}
    >
      {icon && (
        <Box aria-hidden="true" sx={{ display: "flex", flexShrink: 0, color: checked && !disabled ? C.red : C.ink }}>
          {icon}
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
        <Box sx={{ ...TYPE.subHeading, color: C.ink }}>{title}</Box>
        {caption ? <Box sx={{ ...metaSx, color: captionColor || C.muted, mt: 0.25 }}>{caption}</Box> : null}
      </Box>
      {trailing}
    </Box>
  );
}

/** A one-click pill choice. Selected = filled red. */
export function ChoiceChip({ checked, onSelect, children }) {
  return (
    <Box
      component="button"
      type="button"
      role="radio"
      aria-checked={checked ? "true" : "false"}
      tabIndex={checked ? 0 : -1}
      onClick={onSelect}
      sx={{
        ...BUTTON_RESET,
        height: 44,
        px: 2,
        borderRadius: RADIUS.pill,
        border: `1px solid ${checked ? C.red : C.borderStrong}`,
        bgcolor: checked ? C.red : C.bg,
        color: checked ? "#FFFFFF" : C.ink,
        fontFamily: FONT_BODY,
        fontSize: 14,
        fontWeight: 600,
        whiteSpace: "nowrap",
        transition: "background-color .15s ease, border-color .15s ease",
        "&:hover": checked ? { bgcolor: C.redHover } : { borderColor: C.ink },
        ...FOCUS_RING,
      }}
    >
      {children}
    </Box>
  );
}

/** A native select with a visible label. */
export function SelectField({ id, label, value, onChange, disabled, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box component="label" htmlFor={id} sx={{ ...labelSx, display: "block", mb: 0.75 }}>
        {label}
      </Box>
      <Box sx={{ position: "relative" }}>
        <Box
          component="select"
          id={id}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          sx={{ ...fieldSx, appearance: "none", pr: 5, cursor: disabled ? "default" : "pointer" }}
        >
          {children}
        </Box>
        <ExpandMoreRoundedIcon
          aria-hidden="true"
          sx={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 22, color: C.muted, pointerEvents: "none" }}
        />
      </Box>
    </Box>
  );
}

/** On/off switch, green when on. */
export function Switch({ checked, onChange, label, disabled }) {
  return (
    <Box
      component="button"
      type="button"
      role="switch"
      aria-checked={checked ? "true" : "false"}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      sx={{
        ...BUTTON_RESET,
        position: "relative",
        flexShrink: 0,
        width: 48,
        height: 28,
        borderRadius: RADIUS.pill,
        bgcolor: checked ? C.green : C.borderStrong,
        transition: "background-color .2s ease",
        "&::before": { content: '""', position: "absolute", inset: "-8px -2px" },
        "&::after": {
          content: '""',
          position: "absolute",
          top: 3,
          left: checked ? 23 : 3,
          width: 22,
          height: 22,
          borderRadius: "50%",
          bgcolor: "#FFFFFF",
          boxShadow: "0 1px 3px rgba(26,25,24,0.25)",
          transition: "left .2s ease",
        },
        "&:disabled": { opacity: 0.5, cursor: "default" },
        ...FOCUS_RING,
      }}
    />
  );
}
